import { Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'translations/hooks';

import { DateRangeValue } from 'technical/types';
import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';

enum PreselectDateTypeEnum {
  AllTime = 'allTime',
  LastWeek = 'lastWeek',
  LastTwoWeeks = 'lastTwoWeeks',
  LastMonth = 'lastMonth',
}

type PreselectSelectInputProps = {
  value?: DateRangeValue;
  onChange?: (value: DateRangeValue) => void;
  defaultDate: Dayjs;
};

const getInitialRange = (range: DateRangeValue) => {
  if (
    !range ||
    isUndefinedOrNull(range?.[0]) ||
    isUndefinedOrNull(range?.[1])
  ) {
    return PreselectDateTypeEnum.AllTime;
  }

  if (
    dayjs
      .duration(range[1].subtract(dayjs.duration(6, 'days')).diff(range[0]))
      .asMilliseconds() === 0
  ) {
    return PreselectDateTypeEnum.LastWeek;
  }

  if (
    dayjs
      .duration(range[1].subtract(dayjs.duration(13, 'days')).diff(range[0]))
      .asMilliseconds() === 0
  ) {
    return PreselectDateTypeEnum.LastTwoWeeks;
  }

  if (
    dayjs
      .duration(range[1].subtract(dayjs.duration(1, 'month')).diff(range[0]))
      .asMilliseconds() === 0
  ) {
    return PreselectDateTypeEnum.LastMonth;
  }

  return PreselectDateTypeEnum.AllTime;
};

const PreselectDateSelectInput = ({
  value,
  onChange,
  defaultDate,
}: PreselectSelectInputProps) => {
  const { t } = useTranslation();

  const [currentType, setCurrentType] = useState<PreselectDateTypeEnum>(
    value ? getInitialRange(value) : PreselectDateTypeEnum.LastWeek,
  );

  const handleDatesChange = (dates: DateRangeValue) => {
    if (onChange) {
      onChange(dates);
    }
  };

  const handleSelectChange = (type: PreselectDateTypeEnum) => {
    setCurrentType(type);

    switch (type) {
      case PreselectDateTypeEnum.AllTime:
        handleDatesChange([undefined, undefined]);
        break;
      case PreselectDateTypeEnum.LastWeek:
        handleDatesChange([defaultDate.subtract(6, 'day'), defaultDate]);
        break;
      case PreselectDateTypeEnum.LastTwoWeeks:
        handleDatesChange([defaultDate.subtract(13, 'day'), defaultDate]);
        break;
      case PreselectDateTypeEnum.LastMonth:
        handleDatesChange([defaultDate.subtract(1, 'month'), defaultDate]);
        break;
    }
  };

  const options = [
    {
      label: t('time.preselectDateSelect.allTime'),
      value: PreselectDateTypeEnum.AllTime,
    },
    {
      label: t('time.preselectDateSelect.lastWeek'),
      value: PreselectDateTypeEnum.LastWeek,
    },
    {
      label: t('time.preselectDateSelect.lastTwoWeek'),
      value: PreselectDateTypeEnum.LastTwoWeeks,
    },
    {
      label: t('time.preselectDateSelect.lastMonth'),
      value: PreselectDateTypeEnum.LastMonth,
    },
  ];

  return (
    <Select
      value={currentType}
      onChange={handleSelectChange}
      options={options}
      popupMatchSelectWidth={false}
    />
  );
};

enum PreselectHourTypeEnum {
  LastHour = 'lastHour',
  LastSixHours = 'lastSixHours',
  LastTwelveHours = 'lastTwelveHours',
  LastTwentyFourHours = 'lastTwentyFourHours',
}

const PreselectHourSelectInput = ({
  onChange,
  defaultDate,
}: PreselectSelectInputProps) => {
  const { t } = useTranslation();

  const [currentType, setCurrentType] = useState<PreselectHourTypeEnum>(
    PreselectHourTypeEnum.LastHour,
  );

  const handleDatesChange = (dates: DateRangeValue) => {
    if (onChange) {
      onChange(dates);
    }
  };

  const handleSelectChange = (type: PreselectHourTypeEnum) => {
    setCurrentType(type);

    switch (type) {
      case PreselectHourTypeEnum.LastHour:
        handleDatesChange([defaultDate.subtract(1, 'hour'), defaultDate]);
        break;
      case PreselectHourTypeEnum.LastSixHours:
        handleDatesChange([defaultDate.subtract(6, 'hour'), defaultDate]);
        break;
      case PreselectHourTypeEnum.LastTwelveHours:
        handleDatesChange([defaultDate.subtract(12, 'hour'), defaultDate]);
        break;
      case PreselectHourTypeEnum.LastTwentyFourHours:
        handleDatesChange([defaultDate.subtract(24, 'hour'), defaultDate]);
        break;
    }
  };

  const options = [
    {
      label: t('time.preselectDateSelect.lastHour'),
      value: PreselectHourTypeEnum.LastHour,
    },
    {
      label: t('time.preselectDateSelect.lastSixHours'),
      value: PreselectHourTypeEnum.LastSixHours,
    },
    {
      label: t('time.preselectDateSelect.lastTwelveHours'),
      value: PreselectHourTypeEnum.LastTwelveHours,
    },
    {
      label: t('time.preselectDateSelect.lastTwentyFourHours'),
      value: PreselectHourTypeEnum.LastTwentyFourHours,
    },
  ];

  return (
    <Select
      value={currentType}
      onChange={handleSelectChange}
      options={options}
      popupMatchSelectWidth={false}
    />
  );
};

type PreselectTemporalSelectInputProps = {
  mode: 'hour' | 'date';

  value?: DateRangeValue;
  onChange?: (value: DateRangeValue) => void;
  defaultDate: Dayjs;
};

export const PreselectTemporalSelectInput = ({
  mode,
  value,
  onChange,
  defaultDate,
}: PreselectTemporalSelectInputProps) => {
  if (mode === 'hour') {
    return (
      <PreselectHourSelectInput
        value={value}
        onChange={onChange}
        defaultDate={defaultDate}
      />
    );
  }

  return (
    <PreselectDateSelectInput
      value={value}
      onChange={onChange}
      defaultDate={defaultDate}
    />
  );
};
